import styled from 'styled-components';
import React from 'react';
import { Controller } from 'react-hook-form';

import Question from 'components/CustomQuestion/Question';
import useCustomQuestions from 'api/experience/useCustomQuestions/useCustomQuestions';
import { CustomQuestion, CustomQuestionScope } from '@kouto/types';
import { useBrandId } from 'AppProvider';
import {
  CustomQuestionResponse,
  QuestionScope,
  QUESTION_SCOPES,
} from 'types/custom-questions';
import { validateEmpty } from 'utils/validators';
import useSupportedLanguage from 'utils/data/language/useSupportedLanguage';

type CustomQuestionsProps = {
  experienceId: string;
  formStatePath: string;
  scope: QuestionScope;
  title?: string;
};

const groupByHierarchy = (questions: CustomQuestion[]) => {
  return questions.reduce((acc, question) => {
    const key = question.config.conditions?.length ? 'conditional' : 'parent';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(question);
    return acc;
  }, {} as Record<'parent' | 'conditional', CustomQuestion[]>);
};

const CustomQuestions = ({
  experienceId,
  formStatePath,
  scope,
  title,
}: CustomQuestionsProps) => {
  const languageCode = useSupportedLanguage();
  const brandId = useBrandId();

  const { data, isLoading } = useCustomQuestions({
    brandId,
    experienceId,
    language: languageCode,
  });

  if (isLoading || !data) {
    return null;
  }

  // Filter by scope (using new enum)
  const koutoScope =
    scope === QUESTION_SCOPES.PURCHASER
      ? CustomQuestionScope.PURCHASER
      : CustomQuestionScope.PARTICIPANT;

  const scopedQuestions = data.filter((q) => q.config.scope === koutoScope);
  const grouped = groupByHierarchy(scopedQuestions);

  const parentQuestions = grouped.parent || [];
  const conditionalQuestions = grouped.conditional || [];

  const renderConditionalQuestions = (
    parentQuestionId: string,
    parentQuestionResponse?: CustomQuestionResponse[],
  ) => {
    const ownConditionalQuestions = conditionalQuestions.filter((cq) =>
      cq.config.conditions
        ?.map((c) => c.conditionKey)
        .includes(parentQuestionId),
    );

    if (!ownConditionalQuestions.length) {
      return null;
    }

    return ownConditionalQuestions.map((cq) => {
      const conditionQuestionId = cq.id;
      const conditionValues = cq.config.conditions?.map(
        (c) => c.conditionValue,
      );

      const parentResponseKeys = (parentQuestionResponse || []).map(
        (r) => r.key,
      );
      const matchesCondition = (conditionValues || []).some((value) =>
        parentResponseKeys.includes(value),
      );

      if (matchesCondition) {
        return (
          <Controller
            key={conditionQuestionId}
            name={`${formStatePath}.${conditionQuestionId}`}
            rules={
              cq.config.isRequired
                ? { ...validateEmpty(`This field is required`) }
                : {}
            }
            render={({ field: { onChange, value, ref }, formState }) => {
              const errorMessage = formState.errors[
                `${formStatePath}.${conditionQuestionId}`
              ]?.message as string;

              return (
                <Question
                  ref={ref}
                  question={cq}
                  onAnswerChange={onChange}
                  answer={value || []}
                  error={errorMessage}
                />
              );
            }}
          />
        );
      }

      return null;
    });
  };

  return (
    <QuestionsWrapper>
      {scope === QUESTION_SCOPES.PURCHASER &&
        !!parentQuestions.length &&
        title && <Title>{title}</Title>}
      {parentQuestions.map((q) => {
        const parentQuestionId = q.id;
        return (
          <Controller
            key={parentQuestionId}
            name={`${formStatePath}.${parentQuestionId}`}
            rules={
              q.config.isRequired
                ? { ...validateEmpty(`This field is required`) }
                : {}
            }
            render={({ field: { onChange, value, ref }, formState }) => {
              const errorMessage = formState.errors[
                `${formStatePath}.${parentQuestionId}`
              ]?.message as string;
              return (
                <>
                  <Question
                    ref={ref}
                    question={q}
                    onAnswerChange={onChange}
                    answer={value || []}
                    error={errorMessage}
                  />
                  {renderConditionalQuestions(q.id, value)}
                </>
              );
            }}
          />
        );
      })}
    </QuestionsWrapper>
  );
};

export default CustomQuestions;

const QuestionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 24px;
`;

const Title = styled.div`
  font-size: 16px;
  border-bottom: 1px solid gray;
  padding-bottom: 5px;
`;
